import { useState, useEffect } from 'react'
import { fetchTimeline, fetchGoals } from '../utils/api'
import type { ProgressEntry } from '../types/okr'

type TimelineEntry = ProgressEntry & { goalTitle: string; goalLevel: string; goalOwner: string; goalProgress: number }

export default function Timeline() {
  const [entries, setEntries] = useState<TimelineEntry[]>([])
  const [goals, setGoals] = useState<any[]>([])
  const [filter, setFilter] = useState<'team' | 'mine'>('team')
  const [levelFilter, setLevelFilter] = useState<string>('')
  const [dateRange, setDateRange] = useState({ from: '', to: '' })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
  }, [filter, levelFilter, dateRange])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // 加载时间轴数据
      const timelineParams: any = { view: filter }
      if (levelFilter) timelineParams.level = levelFilter
      if (dateRange.from) timelineParams.fromDate = dateRange.from
      if (dateRange.to) timelineParams.toDate = dateRange.to
      
      const timelineData = await fetchTimeline(timelineParams)
      setEntries(timelineData)
      
      // 加载目标数据用于统计
      const goalsData = await fetchGoals()
      setGoals(goalsData)
    } catch (err) {
      setError('加载数据失败')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // 按日期分组
  const groupedByDate: Record<string, TimelineEntry[]> = {}
  entries.forEach(entry => {
    if (!groupedByDate[entry.date]) {
      groupedByDate[entry.date] = []
    }
    groupedByDate[entry.date].push(entry)
  })

  // 按日期倒序排序
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a))

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'meeting': return '○'
      case 'document': return '📄'
      case 'code': return '💻'
      case 'communication': return '💬'
      case 'blocker': return '⚠️'
      case 'milestone': return '🏆'
      default: return '●'
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
    return `${date.getMonth() + 1}月${date.getDate()}日 ${weekdays[date.getDay()]}`
  }

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg">加载中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600">{error}</div>
        <button onClick={loadData} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded">
          重试
        </button>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">进展时间轴</h1>
        
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">视图:</span>
            <button
              onClick={() => setFilter('team')}
              className={`px-3 py-1 rounded ${filter === 'team' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              团队视图
            </button>
            <button
              onClick={() => setFilter('mine')}
              className={`px-3 py-1 rounded ${filter === 'mine' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              我的视图
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">目标层级:</span>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-1 border rounded"
            >
              <option value="">全部</option>
              <option value="weekly">周目标</option>
              <option value="monthly">月目标</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">日期范围:</span>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="px-3 py-1 border rounded"
            />
            <span>至</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="px-3 py-1 border rounded"
            />
            {(dateRange.from || dateRange.to) && (
              <button
                onClick={() => setDateRange({ from: '', to: '' })}
                className="px-3 py-1 text-sm text-gray-600"
              >
                清除
              </button>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          共 {entries.length} 条进展记录
        </div>
      </div>

      <div className="flex gap-6">
        <div className="flex-1">
          {sortedDates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              暂无进展记录
            </div>
          ) : (
            sortedDates.map(date => (
              <div key={date} className="mb-\8">
                <div className="sticky top-0 bg-white z-10 py-2 border-b">
                  <h2 className="text-lg font-semibold">{formatDate(date)}</h2>
                </div>
                <div className="mt-2 space-y-4">
                  {groupedByDate[date].map(entry => (
                    <div key={entry.id} className="border-l-4 border-blue-500 pl-4 py-3">
                      <div className="flex items-start gap-2">
                        <div className="text-gray-500">{getTypeIcon(entry.type)}</div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium">{entry.goalTitle}</h3>
                              <div className="text-sm text-gray-600 mt-1">
                                {entry.content}
                              </div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {entry.timestamp.slice(11, 16)}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <div className="text-gray-600">
                              负责人: {entry.goalOwner}
                            </div>
                            <div className="text-gray-600">
                              当前进度: {entry.goalProgress}%
                            </div>
                            {entry.progressDelta && (
                              <div className={`${entry.progressDelta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {entry.progressDelta > 0 ? '↑' : '↓'} {Math.abs(entry.progressDelta)}%
                              </div>
                            )}
                          </div>

                          {entry.attachments && entry.attachments.length > 0 && (
                            <div className="mt-2 flex gap-2">
                              {entry.attachments.map((att, idx) => (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline"
                                >
                                  📎 {att.name}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="w-64">
          <div className="bg-gray-50 p-4 rounded-lg sticky top-4">
            <h3 className="font-semibold mb-3">本周进展统计</h3>
            
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-600">更新次数</div>
                <div className="text-xl font-bold">{entries.filter(e => {
                  const entryDate = new Date(e.date)
                  const today = new Date()
                  const weekStart = new Date(today.setDate(today.getDate() - today.getDay() + 1))
                  const weekEnd = new Date(weekStart)
                  weekEnd.setDate(weekStart.getDate() + 6)
                  return entryDate >= weekStart && entryDate <= weekEnd
                }).length}</div>
              </div>

              <div>
                <div className="text-sm text-gray-600">进度提升</div>
                <div className="text-xl font-bold text-green-600">
                  +{entries.reduce((sum, e) => sum + (e.progressDelta && e.progressDelta > 0 ? e.progressDelta : 0), 0)}%
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-600">活跃目标</div>
                <div className="mt-2 space-y-1">
                  {goals
                    .filter(g => g.status !== 'completed' && g.progressEntries && g.progressEntries.length > 0)
                    .slice(0, 5)
                    .map(goal => (
                      <div key={goal.id} className="text-sm">
                        <div className="flex justify-between">
                          <span className="truncate">{goal.title}</span>
                          <span>{goal.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div 
                            className="bg-blue-600 h-1.5 rounded-full" 
                            style={{ width: `${goal.progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}